import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Heading,
  Music,
  Plus,
  StickyNote,
  Trash2,
  Wrench,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import {
  addPlanAssignment,
  addPlanItem,
  getServicePlan,
  listSongs,
  removePlanAssignment,
  removePlanItem,
  reorderPlanItems,
  updateServicePlan,
  type PlanAssignmentDto,
  type PlanItemDto,
} from '../../lib/service-planning-api'
import { listPeople } from '../../lib/api'

const itemTypeMeta: Record<
  PlanItemDto['type'],
  { label: string; icon: typeof Music; variant: 'primary' | 'secondary' | 'gold' | 'neutral' }
> = {
  song: { label: 'Song', icon: Music, variant: 'primary' },
  header: { label: 'Header', icon: Heading, variant: 'secondary' },
  note: { label: 'Note', icon: StickyNote, variant: 'gold' },
  element: { label: 'Element', icon: Wrench, variant: 'neutral' },
}

const assignmentStatusVariant: Record<string, 'success' | 'gold' | 'neutral' | 'danger'> = {
  confirmed: 'success',
  accepted: 'success',
  invited: 'gold',
  declined: 'danger',
}

function formatPlanDate(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

function formatLength(seconds: number | null): string {
  if (!seconds) return ''
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s === 0 ? `${m}:00` : `${m}:${String(s).padStart(2, '0')}`
}

// ---------------------------- Running order ----------------------------

function RunningOrder({ planId, items }: { planId: string; items: PlanItemDto[] }) {
  const queryClient = useQueryClient()
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['service-plan', planId] })

  const reorder = useMutation({
    mutationFn: (itemIds: string[]) => reorderPlanItems(planId, { itemIds }),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: (itemId: string) => removePlanItem(planId, itemId),
    onSuccess: invalidate,
  })

  const move = (index: number, dir: -1 | 1) => {
    const next = index + dir
    if (next < 0 || next >= items.length) return
    const ids = items.map((i) => i.id)
    ;[ids[index], ids[next]] = [ids[next]!, ids[index]!]
    reorder.mutate(ids)
  }

  const total = items.reduce((sum, i) => sum + (i.lengthSeconds ?? 0), 0)
  const busy = reorder.isPending || remove.isPending

  return (
    <Card className="overflow-hidden p-0">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Running order</CardTitle>
        <div className="flex items-center gap-2">
          {total > 0 && <span className="text-xs text-dim">{Math.round(total / 60)} min</span>}
          <Badge variant="secondary">{items.length}</Badge>
        </div>
      </CardHeader>
      {items.length === 0 ? (
        <div className="py-14 text-center text-sm text-muted">
          No items yet. Add songs, headers, or notes below.
        </div>
      ) : (
        <ul>
          {items.map((item, index) => {
            const meta = itemTypeMeta[item.type]
            const Icon = meta.icon
            return (
              <li
                key={item.id}
                className="flex items-center gap-3 border-b border-border-soft px-4 py-3 last:border-0"
              >
                <div className="flex flex-col">
                  <button
                    type="button"
                    aria-label="Move up"
                    disabled={busy || index === 0}
                    onClick={() => move(index, -1)}
                    className="text-dim transition-colors hover:text-fg disabled:opacity-30"
                  >
                    <ChevronUp className="size-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Move down"
                    disabled={busy || index === items.length - 1}
                    onClick={() => move(index, 1)}
                    className="text-dim transition-colors hover:text-fg disabled:opacity-30"
                  >
                    <ChevronDown className="size-4" />
                  </button>
                </div>
                <div className="grid size-8 shrink-0 place-items-center rounded-md bg-card text-secondary">
                  <Icon className="size-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{item.title}</span>
                    {item.type === 'song' && item.songTitle && item.songTitle !== item.title && (
                      <span className="truncate text-xs text-dim">({item.songTitle})</span>
                    )}
                  </div>
                  <Badge variant={meta.variant} className="mt-0.5">
                    {meta.label}
                  </Badge>
                </div>
                {item.lengthSeconds != null && (
                  <span className="text-xs tabular-nums text-dim">
                    {formatLength(item.lengthSeconds)}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Remove item"
                  disabled={busy}
                  onClick={() => remove.mutate(item.id)}
                >
                  <Trash2 />
                </Button>
              </li>
            )
          })}
        </ul>
      )}
      <AddItemForm planId={planId} />
    </Card>
  )
}

function AddItemForm({ planId }: { planId: string }) {
  const queryClient = useQueryClient()
  const [type, setType] = useState<PlanItemDto['type']>('song')
  const [title, setTitle] = useState('')
  const [songId, setSongId] = useState('')
  const [minutes, setMinutes] = useState('')

  const songsQuery = useQuery({ queryKey: ['songs'], queryFn: () => listSongs() })

  const add = useMutation({
    mutationFn: () =>
      addPlanItem(planId, {
        type,
        title: title.trim() || (type === 'song' ? selectedSongTitle() : type),
        songId: type === 'song' && songId ? songId : null,
        lengthSeconds: minutes ? Math.round(Number(minutes) * 60) : null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-plan', planId] })
      setTitle('')
      setSongId('')
      setMinutes('')
    },
  })

  const selectedSongTitle = (): string => {
    const s = songsQuery.data?.find((x) => x.id === songId)
    return s?.title ?? 'Song'
  }

  const canSubmit = type === 'song' ? !!songId || !!title.trim() : !!title.trim()

  return (
    <form
      className="flex flex-wrap items-end gap-2 border-t border-border bg-card/40 px-4 py-3"
      onSubmit={(e) => {
        e.preventDefault()
        if (canSubmit) add.mutate()
      }}
    >
      <label className="space-y-1 text-xs">
        <span className="text-dim">Type</span>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as PlanItemDto['type'])}
          className="block h-9 rounded-md border border-border bg-bg-alt px-2 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
        >
          <option value="song">Song</option>
          <option value="header">Header</option>
          <option value="note">Note</option>
          <option value="element">Element</option>
        </select>
      </label>

      {type === 'song' && (
        <label className="space-y-1 text-xs">
          <span className="text-dim">Song</span>
          <select
            value={songId}
            onChange={(e) => setSongId(e.target.value)}
            className="block h-9 min-w-44 rounded-md border border-border bg-bg-alt px-2 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
          >
            <option value="">Select a song…</option>
            {(songsQuery.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="flex-1 space-y-1 text-xs">
        <span className="text-dim">{type === 'song' ? 'Title (optional)' : 'Title'}</span>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={type === 'song' ? 'Defaults to song title' : 'e.g. Welcome & Announcements'}
          className="h-9"
        />
      </label>

      <label className="space-y-1 text-xs">
        <span className="text-dim">Min</span>
        <Input
          type="number"
          min={0}
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          className="h-9 w-20"
          placeholder="4"
        />
      </label>

      <Button type="submit" size="sm" disabled={add.isPending || !canSubmit}>
        {add.isPending ? <Spinner /> : <Plus />}
        Add
      </Button>
    </form>
  )
}

// ---------------------------- Assignments ----------------------------

function AssignmentsCard({
  planId,
  assignments,
}: {
  planId: string
  assignments: PlanAssignmentDto[]
}) {
  const queryClient = useQueryClient()
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['service-plan', planId] })

  const peopleQuery = useQuery({ queryKey: ['people'], queryFn: listPeople })

  const [personId, setPersonId] = useState('')
  const [position, setPosition] = useState('')

  const add = useMutation({
    mutationFn: () => addPlanAssignment(planId, { personId, position: position.trim() }),
    onSuccess: () => {
      invalidate()
      setPersonId('')
      setPosition('')
    },
  })
  const remove = useMutation({
    mutationFn: (id: string) => removePlanAssignment(planId, id),
    onSuccess: invalidate,
  })

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Serving</CardTitle>
        <Badge variant="secondary">{assignments.length}</Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        {assignments.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted">No one assigned yet.</p>
        ) : (
          <ul className="space-y-2">
            {assignments.map((a) => (
              <li
                key={a.id}
                className="flex items-center justify-between gap-3 rounded-lg bg-card px-3 py-2.5"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{a.personName ?? 'Unknown'}</div>
                  <div className="truncate text-xs text-dim">{a.position}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={assignmentStatusVariant[a.status] ?? 'neutral'}
                    className="capitalize"
                  >
                    {a.status}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Remove assignment"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(a.id)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <form
          className="space-y-2 border-t border-border pt-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (personId && position.trim()) add.mutate()
          }}
        >
          <label className="block space-y-1 text-xs">
            <span className="text-dim">Person</span>
            <select
              value={personId}
              onChange={(e) => setPersonId(e.target.value)}
              className="block h-9 w-full rounded-md border border-border bg-bg-alt px-2 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
            >
              <option value="">Select a person…</option>
              {(peopleQuery.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {[p.firstName, p.lastName].filter(Boolean).join(' ') || p.email || p.id}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1 text-xs">
            <span className="text-dim">Position</span>
            <Input
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="e.g. Worship Leader, Drums, Sound"
              className="h-9"
            />
          </label>
          <Button
            type="submit"
            size="sm"
            className="w-full"
            disabled={add.isPending || !personId || !position.trim()}
          >
            {add.isPending ? <Spinner /> : <Plus />}
            Assign
          </Button>
          {add.isError && (
            <p className="text-xs text-danger">
              {add.error instanceof Error ? add.error.message : 'Could not assign.'}
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  )
}

// ---------------------------- Page ----------------------------

export function PlanEditor() {
  const { id = '' } = useParams<{ id: string }>()
  const queryClient = useQueryClient()

  const planQuery = useQuery({
    queryKey: ['service-plan', id],
    queryFn: () => getServicePlan(id),
    enabled: !!id,
  })

  const togglePublish = useMutation({
    mutationFn: (status: 'draft' | 'published') => updateServicePlan(id, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-plan', id] })
      void queryClient.invalidateQueries({ queryKey: ['service-plans'] })
    },
  })

  if (planQuery.isLoading) {
    return (
      <Card className="flex items-center justify-center gap-2 py-20 text-muted">
        <Spinner /> Loading plan…
      </Card>
    )
  }
  if (planQuery.isError || !planQuery.data) {
    return (
      <Card className="space-y-2 py-20 text-center">
        <p className="text-sm text-danger">Couldn’t load this service plan.</p>
        <Link
          to="/app/service-planning"
          className="text-xs text-secondary underline-offset-4 hover:underline"
        >
          Back to service plans
        </Link>
      </Card>
    )
  }

  const plan = planQuery.data
  const isPublished = plan.status === 'published'

  return (
    <div className="space-y-6">
      <Link
        to="/app/service-planning"
        className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-fg"
      >
        <ArrowLeft className="size-4" /> Service plans
      </Link>

      <PageHeader
        title={plan.title}
        description={`${formatPlanDate(plan.planDate)}${plan.siteName ? ` · ${plan.siteName}` : ''}`}
        actions={
          <>
            <Badge variant={isPublished ? 'success' : 'neutral'} className="capitalize">
              {plan.status}
            </Badge>
            <Button
              variant={isPublished ? 'secondary' : 'primary'}
              disabled={togglePublish.isPending}
              onClick={() => togglePublish.mutate(isPublished ? 'draft' : 'published')}
            >
              {togglePublish.isPending ? <Spinner /> : null}
              {isPublished ? 'Unpublish' : 'Publish'}
            </Button>
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <RunningOrder planId={id} items={plan.items} />
        </div>
        <div>
          <AssignmentsCard planId={id} assignments={plan.assignments} />
        </div>
      </div>
    </div>
  )
}
